import React, { useState } from "react";
import {
  CheckCircle,
  Settings,
  Calendar,
  RefreshCw,
  Link as LinkIcon,
  AlertCircle,
} from "lucide-react";
import { useNavigate } from "react-router";

const accountingSystems = [
  {
    id: "1c",
    name: "1С: Бухгалтерия",
    description: "Самая популярная система учета в Казахстане",
    logo: "/1c.png",
    color: "#FFD700",
    features: [
      "Автоматическая выгрузка данных",
      "Синхронизация контрагентов",
      "Экспорт актов сверки",
    ],
  },
  {
    id: "umit",
    name: "UMIT",
    description: "Облачная система бухгалтерского учета",
    logo: "U",
    color: "#4169E1",
    features: [
      "REST API интеграция",
      "Онлайн синхронизация",
      "Автоматические отчеты",
    ],
  },
  {
    id: "armbuh",
    name: "АРМ Бухгалтер",
    description: "Профессиональное решение для бухгалтерии",
    logo: "АБ",
    color: "#32CD32",
    features: [
      "Импорт/экспорт данных",
      "Интеграция с банками",
      "Управление документами",
    ],
  },
];

const checkFrequencies = [
  {
    id: "daily",
    name: "Ежедневно",
    description: "Проверка каждый день в 9:00",
    icon: RefreshCw,
    recommended: true,
  },
  {
    id: "weekly",
    name: "Еженедельно",
    description: "Проверка каждый понедельник",
    icon: Calendar,
    recommended: false,
  },
  {
    id: "monthly",
    name: "Ежемесячно",
    description: "Проверка 1-го числа каждого месяца",
    icon: Calendar,
    recommended: false,
  },
];

export const IntegrationSetup = () => {
  const navigate = useNavigate();

  const [selectedSystem, setSelectedSystem] = useState(null);
  const [selectedFrequency, setSelectedFrequency] = useState("daily");
  const [isConnected, setIsConnected] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [serverUrl, setServerUrl] = useState("");
  const [showApiFields, setShowApiFields] = useState(false);

  const handleConnect = () => {
    if (!selectedSystem) {
      alert("Пожалуйста, выберите бухгалтерскую систему");
      return;
    }

    // Имитация подключения
    setTimeout(() => {
      setIsConnected(true);
      alert(
        `Успешно подключено к ${
          accountingSystems.find((s) => s.id === selectedSystem).name
        }!`
      );
    }, 1000);
  };

  const handleSave = () => {
    navigate("/upload");
  };

  const styles = {
    container: {
      minHeight: "100vh",
      background: "#ffffff",
      padding: "40px 20px",
      marginTop: "20px",
      borderRadius: "10px",
    },
    card: {
      maxWidth: "1200px",
      margin: "0 auto",
      backgroundColor: "white",
      borderRadius: "16px",
      boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      overflow: "hidden",
    },
    header: {
      background: "linear-gradient(135deg, #0077ffb7 0%, #0078FF 100%)",
      padding: "32px",
      color: "white",
    },
    headerTitle: {
      fontSize: "28px",
      fontWeight: "bold",
      marginBottom: "8px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    headerSubtitle: {
      fontSize: "16px",
      opacity: 0.9,
    },
    content: {
      padding: "32px",
    },
    section: {
      marginBottom: "40px",
    },
    sectionTitle: {
      fontSize: "20px",
      fontWeight: "bold",
      color: "#111827",
      marginBottom: "16px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    sectionDescription: {
      fontSize: "14px",
      color: "#6b7280",
      marginBottom: "20px",
    },
    systemsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "20px",
      marginBottom: "24px",
    },
    systemCard: {
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      padding: "24px",
      cursor: "pointer",
      transition: "all 0.3s",
      position: "relative",
    },
    systemCardSelected: {
      border: "2px solid #667eea",
      borderRadius: "12px",
      padding: "24px",
      cursor: "pointer",
      transition: "all 0.3s",
      position: "relative",
      backgroundColor: "#f0f4ff",
      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.2)",
    },
    systemHeader: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      marginBottom: "12px",
    },
    systemLogo: {
      width: "60px",
      height: "60px",
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "24px",
      fontWeight: "bold",
      color: "white",
    },
    systemInfo: {
      flex: 1,
    },
    systemName: {
      fontSize: "18px",
      fontWeight: "bold",
      color: "#111827",
      marginBottom: "4px",
    },
    systemDescription: {
      fontSize: "13px",
      color: "#6b7280",
    },
    checkmark: {
      position: "absolute",
      top: "16px",
      right: "16px",
      color: "#667eea",
    },
    featuresList: {
      listStyle: "none",
      padding: 0,
      margin: 0,
    },
    featureItem: {
      fontSize: "13px",
      color: "#374151",
      marginBottom: "6px",
      paddingLeft: "20px",
      position: "relative",
    },
    featureBullet: {
      position: "absolute",
      left: 0,
      color: "#667eea",
    },
    apiSection: {
      backgroundColor: "#f9fafb",
      border: "1px solid #e5e7eb",
      borderRadius: "12px",
      padding: "20px",
      marginTop: "20px",
    },
    apiHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "16px",
    },
    apiTitle: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#111827",
    },
    toggleButton: {
      padding: "6px 12px",
      backgroundColor: "#0078FF",
      color: "white",
      border: "none",
      borderRadius: "6px",
      fontSize: "13px",
      cursor: "pointer",
      transition: "background-color 0.2s",
    },
    inputGroup: {
      marginBottom: "16px",
    },
    label: {
      display: "block",
      fontSize: "14px",
      fontWeight: "500",
      color: "#374151",
      marginBottom: "6px",
    },
    input: {
      width: "100%",
      padding: "10px 12px",
      fontSize: "14px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      outline: "none",
      transition: "border-color 0.2s",
      boxSizing: "border-box",
    },
    frequencyGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "16px",
    },
    frequencyCard: {
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      padding: "20px",
      cursor: "pointer",
      transition: "all 0.3s",
      position: "relative",
    },
    frequencyCardSelected: {
      border: "2px solid #667eea",
      borderRadius: "12px",
      padding: "20px",
      cursor: "pointer",
      transition: "all 0.3s",
      position: "relative",
      backgroundColor: "#f0f4ff",
    },
    frequencyHeader: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      marginBottom: "8px",
    },
    frequencyName: {
      fontSize: "16px",
      fontWeight: "bold",
      color: "#111827",
    },
    frequencyDescription: {
      fontSize: "13px",
      color: "#6b7280",
    },
    recommendedBadge: {
      display: "inline-block",
      padding: "4px 10px",
      backgroundColor: "#d1fae5",
      color: "#065f46",
      fontSize: "11px",
      fontWeight: "600",
      borderRadius: "12px",
      marginTop: "8px",
    },
    infoBox: {
      display: "flex",
      gap: "12px",
      padding: "16px",
      backgroundColor: "#eff6ff",
      border: "1px solid #bfdbfe",
      borderRadius: "8px",
      marginBottom: "24px",
    },
    infoText: {
      fontSize: "14px",
      color: "#1e40af",
      lineHeight: "1.5",
    },
    connectButton: {
      width: "100%",
      padding: "14px",
      backgroundColor: "#0078FF",
      color: "white",
      border: "none",
      borderRadius: "8px",
      fontSize: "16px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "background-color 0.2s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      marginTop: "20px",
    },
    connectedButton: {
      width: "100%",
      padding: "14px",
      backgroundColor: "#2ECC71",
      color: "white",
      border: "none",
      borderRadius: "8px",
      fontSize: "16px",
      fontWeight: "600",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      marginTop: "20px",
    },
    saveButton: {
      width: "100%",
      padding: "16px",
      backgroundColor: "#2ECC71",
      color: "white",
      border: "none",
      borderRadius: "8px",
      fontSize: "18px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "background-color 0.2s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
    },
    disabledButton: {
      width: "100%",
      padding: "16px",
      backgroundColor: "#d1d5db",
      color: "#9ca3af",
      border: "none",
      borderRadius: "8px",
      fontSize: "18px",
      fontWeight: "600",
      cursor: "not-allowed",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
    },
  };

  return (
    <div className={styles.wrapper}>
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <h1 style={styles.headerTitle}>
              <Settings size={32} />
              Интеграция с бухгалтерией
            </h1>
            <p style={styles.headerSubtitle}>
              Подключите вашу бухгалтерскую систему для автоматизации
              взаимозачётов
            </p>
          </div>

          <div style={styles.content}>
            {/* Выбор бухгалтерской системы */}
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>
                <LinkIcon size={24} />
                Выберите бухгалтерскую систему
              </h2>
              <p style={styles.sectionDescription}>
                Выберите систему учета, которую вы используете в своей компании
              </p>

              <div style={styles.systemsGrid}>
                {accountingSystems.map((system) => (
                  <div
                    key={system.id}
                    style={
                      selectedSystem === system.id
                        ? styles.systemCardSelected
                        : styles.systemCard
                    }
                    onClick={() => setSelectedSystem(system.id)}
                    onMouseEnter={(e) => {
                      if (selectedSystem !== system.id) {
                        e.currentTarget.style.borderColor = "#d1d5db";
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedSystem !== system.id) {
                        e.currentTarget.style.borderColor = "#e5e7eb";
                        e.currentTarget.style.transform = "translateY(0)";
                      }
                    }}
                  >
                    {selectedSystem === system.id && (
                      <CheckCircle size={24} style={styles.checkmark} />
                    )}

                    <div style={styles.systemHeader}>
                      <div
                        style={{
                          ...styles.systemLogo,
                          backgroundColor: system.color,
                        }}
                      >
                        {system.logo.includes("/") ? (
                          <img src={system.logo} width={"100%"} />
                        ) : (
                          system.logo
                        )}
                      </div>
                      <div style={styles.systemInfo}>
                        <div style={styles.systemName}>{system.name}</div>
                        <div style={styles.systemDescription}>
                          {system.description}
                        </div>
                      </div>
                    </div>

                    <ul style={styles.featuresList}>
                      {system.features.map((feature, index) => (
                        <li key={index} style={styles.featureItem}>
                          <span style={styles.featureBullet}>•</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {selectedSystem && (
                <div style={styles.apiSection}>
                  <div style={styles.apiHeader}>
                    <span style={styles.apiTitle}>Настройки подключения</span>
                    <button
                      style={styles.toggleButton}
                      onClick={() => setShowApiFields(!showApiFields)}
                      onMouseEnter={(e) =>
                        (e.target.style.backgroundColor = "#5568d3")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.backgroundColor = "#667eea")
                      }
                    >
                      {showApiFields ? "Скрыть" : "Показать"}
                    </button>
                  </div>

                  {showApiFields && (
                    <>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>URL сервера</label>
                        <input
                          type="text"
                          value={serverUrl}
                          onChange={(e) => setServerUrl(e.target.value)}
                          placeholder="https://your-server.kz/api"
                          style={styles.input}
                          onFocus={(e) =>
                            (e.target.style.borderColor = "#667eea")
                          }
                          onBlur={(e) =>
                            (e.target.style.borderColor = "#d1d5db")
                          }
                        />
                      </div>

                      <div style={styles.inputGroup}>
                        <label style={styles.label}>API ключ</label>
                        <input
                          type="password"
                          value={apiKey}
                          onChange={(e) => setApiKey(e.target.value)}
                          placeholder="Введите API ключ"
                          style={styles.input}
                          onFocus={(e) =>
                            (e.target.style.borderColor = "#667eea")
                          }
                          onBlur={(e) =>
                            (e.target.style.borderColor = "#d1d5db")
                          }
                        />
                      </div>
                    </>
                  )}

                  <button
                    onClick={handleConnect}
                    style={
                      isConnected
                        ? styles.connectedButton
                        : styles.connectButton
                    }
                    onMouseEnter={(e) => {
                      if (!isConnected) {
                        e.target.style.backgroundColor = "#5568d3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isConnected) {
                        e.target.style.backgroundColor = "#667eea";
                      }
                    }}
                  >
                    {isConnected ? (
                      <>
                        <CheckCircle size={20} />
                        Подключено
                      </>
                    ) : (
                      <>
                        <LinkIcon size={20} />
                        Подключить систему
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Частота проверки */}
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>
                <Calendar size={24} />
                Частота проверки взаимозачётов
              </h2>
              <p style={styles.sectionDescription}>
                Выберите, как часто система должна проверять возможности для
                взаимозачётов
              </p>

              <div style={styles.infoBox}>
                <AlertCircle
                  size={20}
                  color="#1e40af"
                  style={{ flexShrink: 0, marginTop: "2px" }}
                />
                <p style={styles.infoText}>
                  Рекомендуем ежедневную проверку для своевременного выявления
                  возможностей взаимозачёта и оптимизации денежных потоков.
                </p>
              </div>

              <div style={styles.frequencyGrid}>
                {checkFrequencies.map((frequency) => {
                  const Icon = frequency.icon;
                  return (
                    <div
                      key={frequency.id}
                      style={
                        selectedFrequency === frequency.id
                          ? styles.frequencyCardSelected
                          : styles.frequencyCard
                      }
                      onClick={() => setSelectedFrequency(frequency.id)}
                      onMouseEnter={(e) => {
                        if (selectedFrequency !== frequency.id) {
                          e.currentTarget.style.borderColor = "#d1d5db";
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedFrequency !== frequency.id) {
                          e.currentTarget.style.borderColor = "#e5e7eb";
                          e.currentTarget.style.transform = "translateY(0)";
                        }
                      }}
                    >
                      {selectedFrequency === frequency.id && (
                        <CheckCircle size={20} style={styles.checkmark} />
                      )}

                      <div style={styles.frequencyHeader}>
                        <Icon size={24} color="#667eea" />
                        <span style={styles.frequencyName}>
                          {frequency.name}
                        </span>
                      </div>
                      <p style={styles.frequencyDescription}>
                        {frequency.description}
                      </p>

                      {frequency.recommended && (
                        <span style={styles.recommendedBadge}>
                          Рекомендуется
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Кнопка сохранения */}
            <button
              onClick={handleSave}
              disabled={!selectedSystem || !isConnected}
              style={
                !selectedSystem || !isConnected
                  ? styles.disabledButton
                  : styles.saveButton
              }
              onMouseEnter={(e) => {
                if (selectedSystem && isConnected) {
                  e.target.style.backgroundColor = "#059669";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedSystem && isConnected) {
                  e.target.style.backgroundColor = "#10b981";
                }
              }}
            >
              <CheckCircle size={22} />
              Сохранить настройки
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
